/**
 * Mock Data — Integrations Redesign (INT-02)
 *
 * Covers all scenarios specified in the PRD mock data strategy:
 *   ✅ Company with both integrations connected
 *   ✅ Company with only Financial connected
 *   ✅ Company with no integrations
 *   ✅ Company with users assigned
 *   ✅ Company with no users assigned
 *   ✅ Inactive company
 *
 * Swap from mock to real API: change USE_MOCK_INTEGRATIONS_DATA to false
 * (or wire via environment flag) in the service layer (INT-03).
 */

import type {
  CompanyWithIntegrations,
  CompanyUser,
  EmployeeCompanyAccessRow,
} from '@/types';

// ---------------------------------------------------------------------------
// Simulated async delay (mirrors real API latency)
// ---------------------------------------------------------------------------
export const delay = (ms = 500) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// ---------------------------------------------------------------------------
// Companies — CompanyWithIntegrations[]
// ---------------------------------------------------------------------------

/**
 * Mock companies covering all PRD scenarios.
 * Sorted: Active companies first (alphabetical), then Inactive (alphabetical).
 */
export const mockCompaniesWithIntegrations: CompanyWithIntegrations[] = [
  // --- Active, both integrations connected + project connected ---
  {
    id: 'company-1',
    name: 'Acme Corporation',
    code: 'ACME',
    status: 'active',
    financialIntegration: {
      product: 'xero',
      externalInstanceId: 'xero-db-001',
      status: 'connected',
      lastSyncAt: '2025-01-15T08:30:00Z',
    },
    hrIntegration: {
      product: 'people-hr',
      externalInstanceId: 'phr-db-001',
      status: 'connected',
      lastSyncAt: '2025-01-15T09:00:00Z',
    },
    projectIntegration: {
      product: 'access-projects',
      externalInstanceId: 'ap-db-001',
      status: 'connected',
      lastSyncAt: '2025-01-15T09:15:00Z',
    },
    plannerIntegration: {
      product: 'access-resource-scheduler',
      externalInstanceId: 'rs-db-001',
      status: 'connected',
      lastSyncAt: '2025-01-15T09:30:00Z',
    },
    userCount: 4,
  },
  // --- Active, only Financial connected (FocalPoint for project) ---
  {
    id: 'company-2',
    name: 'Beta Limited',
    code: 'BETA',
    status: 'active',
    financialIntegration: {
      product: 'access-financials',
      externalInstanceId: 'af-db-002',
      status: 'connected',
      lastSyncAt: '2025-01-14T16:45:00Z',
    },
    hrIntegration: {
      product: null,
      externalInstanceId: null,
      status: 'not_configured',
      lastSyncAt: null,
    },
    projectIntegration: {
      product: 'access-focalpoint',
      externalInstanceId: 'fp-db-002',
      status: 'connected',
      lastSyncAt: '2025-01-14T17:00:00Z',
    },
    plannerIntegration: {
      product: null,
      externalInstanceId: null,
      status: 'not_configured',
      lastSyncAt: null,
    },
    userCount: 2,
  },
  // --- Active, Financial connected with error on HR ---
  {
    id: 'company-3',
    name: 'Delta Enterprises',
    code: 'DELTA',
    status: 'active',
    financialIntegration: {
      product: 'sage',
      externalInstanceId: 'sage-db-003',
      status: 'connected',
      lastSyncAt: '2025-01-13T12:00:00Z',
    },
    hrIntegration: {
      product: 'bamboohr',
      externalInstanceId: 'bhr-db-003',
      status: 'error',
      lastSyncAt: '2025-01-10T08:00:00Z',
    },
    projectIntegration: {
      product: 'access-financials-operations',
      externalInstanceId: 'finops-db-003',
      status: 'not_connected',
      lastSyncAt: null,
    },
    plannerIntegration: {
      product: 'access-planner',
      externalInstanceId: 'planner-db-003',
      status: 'not_connected',
      lastSyncAt: null,
    },
    userCount: 3,
  },
  // --- Active, no integrations configured ---
  {
    id: 'company-4',
    name: 'Epsilon Solutions',
    code: 'EPSILON',
    status: 'active',
    financialIntegration: {
      product: null,
      externalInstanceId: null,
      status: 'not_configured',
      lastSyncAt: null,
    },
    hrIntegration: {
      product: null,
      externalInstanceId: null,
      status: 'not_configured',
      lastSyncAt: null,
    },
    projectIntegration: {
      product: null,
      externalInstanceId: null,
      status: 'not_configured',
      lastSyncAt: null,
    },
    plannerIntegration: {
      product: null,
      externalInstanceId: null,
      status: 'not_configured',
      lastSyncAt: null,
    },
    userCount: 0,
  },
  // --- Active, both connected — larger user count ---
  {
    id: 'company-5',
    name: 'Zeta Technologies',
    code: 'ZETA',
    status: 'active',
    financialIntegration: {
      product: 'quickbooks',
      externalInstanceId: 'qb-db-005',
      status: 'connected',
      lastSyncAt: '2025-01-15T07:15:00Z',
    },
    hrIntegration: {
      product: 'workday',
      externalInstanceId: 'wd-db-005',
      status: 'connected',
      lastSyncAt: '2025-01-15T07:30:00Z',
    },
    projectIntegration: {
      product: 'access-projects',
      externalInstanceId: 'ap-db-005',
      status: 'connected',
      lastSyncAt: '2025-01-15T07:45:00Z',
    },
    plannerIntegration: {
      product: 'access-planner',
      externalInstanceId: 'planner-db-005',
      status: 'connected',
      lastSyncAt: '2025-01-15T08:00:00Z',
    },
    userCount: 6,
  },
  // --- Inactive company (no integrations) ---
  {
    id: 'company-6',
    name: 'Gamma Industries',
    code: 'GAMMA',
    status: 'inactive',
    financialIntegration: {
      product: 'access-dimensions',
      externalInstanceId: 'ad-db-006',
      status: 'not_connected',
      lastSyncAt: null,
    },
    hrIntegration: {
      product: null,
      externalInstanceId: null,
      status: 'not_configured',
      lastSyncAt: null,
    },
    projectIntegration: {
      product: null,
      externalInstanceId: null,
      status: 'not_configured',
      lastSyncAt: null,
    },
    plannerIntegration: {
      product: null,
      externalInstanceId: null,
      status: 'not_configured',
      lastSyncAt: null,
    },
    userCount: 1,
  },
];

// ---------------------------------------------------------------------------
// Users per Company — Record<companyId, CompanyUser[]>
// ---------------------------------------------------------------------------

/**
 * Mock users assigned to each company.
 * Covers: companies with multiple users, one user, and no users.
 */
export const mockCompanyUsers: Record<string, CompanyUser[]> = {
  'company-1': [
    {
      id: 'user-1',
      name: 'Alice Johnson',
      email: 'alice.johnson@acme.com',
      role: 'Finance Manager',
      photoUrl: 'https://picsum.photos/seed/alice-johnson/300/300',
      isDefault: false,
    },
    {
      id: 'user-2',
      name: 'Bob Smith',
      email: 'bob.smith@acme.com',
      role: 'HR Administrator',
      photoUrl: 'https://picsum.photos/seed/bob-smith/300/300',
      isDefault: false,
    },
    {
      id: 'user-3',
      name: 'Carol White',
      email: 'carol.white@acme.com',
      role: 'Project Manager',
      photoUrl: 'https://picsum.photos/seed/carol-white/300/300',
      isDefault: false,
    },
    {
      id: 'user-4',
      name: 'Dan Brown',
      email: 'dan.brown@acme.com',
      role: 'Developer',
      photoUrl: 'https://picsum.photos/seed/dan-brown/300/300',
      isDefault: false,
    },
  ],
  'company-2': [
    {
      id: 'user-1',
      name: 'Alice Johnson',
      email: 'alice.johnson@acme.com',
      role: 'Finance Manager',
      photoUrl: 'https://picsum.photos/seed/alice-johnson/300/300',
      isDefault: false,
    },
    {
      id: 'user-5',
      name: 'Eve Davis',
      email: 'eve.davis@beta.com',
      role: 'Operations Lead',
      photoUrl: 'https://picsum.photos/seed/eve-davis/300/300',
      isDefault: false,
    },
  ],
  'company-3': [
    {
      id: 'user-2',
      name: 'Bob Smith',
      email: 'bob.smith@acme.com',
      role: 'HR Administrator',
      photoUrl: 'https://picsum.photos/seed/bob-smith/300/300',
      isDefault: false,
    },
    {
      id: 'user-6',
      name: 'Frank Miller',
      email: 'frank.miller@delta.com',
      role: 'Finance Director',
      photoUrl: 'https://picsum.photos/seed/frank-miller/300/300',
      isDefault: false,
    },
    {
      id: 'user-7',
      name: 'Grace Lee',
      email: 'grace.lee@delta.com',
      role: 'Analyst',
      photoUrl: 'https://picsum.photos/seed/grace-lee/300/300',
      isDefault: false,
    },
  ],
  // company-4: no users assigned (Epsilon Solutions — new company)
  'company-4': [],
  'company-5': [
    {
      id: 'user-1',
      name: 'Alice Johnson',
      email: 'alice.johnson@acme.com',
      role: 'Finance Manager',
      photoUrl: 'https://picsum.photos/seed/alice-johnson/300/300',
      isDefault: false,
    },
    {
      id: 'user-3',
      name: 'Carol White',
      email: 'carol.white@acme.com',
      role: 'Project Manager',
      photoUrl: 'https://picsum.photos/seed/carol-white/300/300',
      isDefault: false,
    },
    {
      id: 'user-8',
      name: 'Harry Wilson',
      email: 'harry.wilson@zeta.com',
      role: 'CTO',
      photoUrl: 'https://picsum.photos/seed/harry-wilson/300/300',
      isDefault: false,
    },
    {
      id: 'user-9',
      name: 'Iris Chen',
      email: 'iris.chen@zeta.com',
      role: 'Developer',
      photoUrl: 'https://picsum.photos/seed/iris-chen/300/300',
      isDefault: false,
    },
    {
      id: 'user-10',
      name: 'Jack Taylor',
      email: 'jack.taylor@zeta.com',
      role: 'Developer',
      photoUrl: 'https://picsum.photos/seed/jack-taylor/300/300',
      isDefault: false,
    },
    {
      id: 'user-11',
      name: 'Karen Moore',
      email: 'karen.moore@zeta.com',
      role: 'Designer',
      photoUrl: 'https://picsum.photos/seed/karen-moore/300/300',
      isDefault: false,
    },
  ],
  'company-6': [
    {
      id: 'user-12',
      name: 'Leo Turner',
      email: 'leo.turner@gamma.com',
      role: 'Administrator',
      photoUrl: 'https://picsum.photos/seed/leo-turner/300/300',
      isDefault: false,
    },
  ],
};

// ---------------------------------------------------------------------------
// All users available to add to a company (search pool)
// ---------------------------------------------------------------------------

/**
 * The full pool of users that can be searched and added to a company.
 */
export const mockAllUsers: CompanyUser[] = [
  { id: 'user-1',  name: 'Alice Johnson', email: 'alice.johnson@acme.com',   role: 'Finance Manager',  isDefault: false },
  { id: 'user-2',  name: 'Bob Smith',     email: 'bob.smith@acme.com',       role: 'HR Administrator', isDefault: false },
  { id: 'user-3',  name: 'Carol White',   email: 'carol.white@acme.com',     role: 'Project Manager',  isDefault: false },
  { id: 'user-4',  name: 'Dan Brown',     email: 'dan.brown@acme.com',       role: 'Developer',        isDefault: false },
  { id: 'user-5',  name: 'Eve Davis',     email: 'eve.davis@beta.com',       role: 'Operations Lead',  isDefault: false },
  { id: 'user-6',  name: 'Frank Miller',  email: 'frank.miller@delta.com',   role: 'Finance Director', isDefault: false },
  { id: 'user-7',  name: 'Grace Lee',     email: 'grace.lee@delta.com',      role: 'Analyst',          isDefault: false },
  { id: 'user-8',  name: 'Harry Wilson',  email: 'harry.wilson@zeta.com',    role: 'CTO',              isDefault: false },
  { id: 'user-9',  name: 'Iris Chen',     email: 'iris.chen@zeta.com',       role: 'Developer',        isDefault: false },
  { id: 'user-10', name: 'Jack Taylor',   email: 'jack.taylor@zeta.com',     role: 'Developer',        isDefault: false },
  { id: 'user-11', name: 'Karen Moore',   email: 'karen.moore@zeta.com',     role: 'Designer',         isDefault: false },
  { id: 'user-12', name: 'Leo Turner',    email: 'leo.turner@gamma.com',     role: 'Administrator',    isDefault: false },
  { id: 'user-13', name: 'Mia Scott',     email: 'mia.scott@org.com',        role: 'HR Manager',       isDefault: false },
  { id: 'user-14', name: 'Noah Evans',    email: 'noah.evans@org.com',       role: 'Accountant',       isDefault: false },
  { id: 'user-15', name: 'Olivia King',   email: 'olivia.king@org.com',      role: 'Business Analyst', isDefault: false },
];

// ---------------------------------------------------------------------------
// Employee Company Access (per employee, for Team Members tab — INT-10)
// ---------------------------------------------------------------------------

/**
 * Returns a mock company access list for a given employee.
 * Each row represents one company in the org, with access + default flags.
 */
export function getMockEmployeeCompanyAccess(employeeId: string): EmployeeCompanyAccessRow[] {
  // Define which companies each employee has access to by default
  const accessMap: Record<string, { companyIds: string[]; defaultCompanyId: string | null }> = {
    'user-1': { companyIds: ['company-1', 'company-2', 'company-5'], defaultCompanyId: 'company-1' },
    'user-2': { companyIds: ['company-1', 'company-3'],              defaultCompanyId: 'company-1' },
    'user-3': { companyIds: ['company-1', 'company-5'],              defaultCompanyId: 'company-5' },
    'user-4': { companyIds: ['company-1'],                           defaultCompanyId: 'company-1' },
    'user-5': { companyIds: ['company-2'],                           defaultCompanyId: 'company-2' },
    'user-6': { companyIds: ['company-3'],                           defaultCompanyId: 'company-3' },
    'user-7': { companyIds: ['company-3'],                           defaultCompanyId: 'company-3' },
    'user-8': { companyIds: ['company-5'],                           defaultCompanyId: 'company-5' },
  };

  const employeeAccess = accessMap[employeeId] ?? { companyIds: [], defaultCompanyId: null };

  return mockCompaniesWithIntegrations.map((company) => ({
    companyId: company.id,
    companyName: company.name,
    companyCode: company.code,
    companyStatus: company.status,
    hasAccess: employeeAccess.companyIds.includes(company.id),
    isDefault: company.id === employeeAccess.defaultCompanyId,
  }));
}

// ---------------------------------------------------------------------------
// Mutable in-memory store (allows add/remove to persist during session)
// ---------------------------------------------------------------------------

/**
 * In-memory mutable clone of the company users map.
 * Mutations (add/remove user) operate on this store.
 */
let _companyUsersStore: Record<string, CompanyUser[]> = JSON.parse(
  JSON.stringify(mockCompanyUsers)
);

/**
 * In-memory mutable clone of companies.
 */
let _companiesStore: CompanyWithIntegrations[] = mockCompaniesWithIntegrations.map((c) => ({ ...c }));

/**
 * Reset stores to initial mock data (useful in tests).
 */
export function resetMockStores() {
  _companyUsersStore = JSON.parse(JSON.stringify(mockCompanyUsers));
  _companiesStore = mockCompaniesWithIntegrations.map((c) => ({ ...c }));
}

// ---------------------------------------------------------------------------
// Mock Service Functions
// ---------------------------------------------------------------------------

/**
 * Mock service for the integrations redesign.
 * All functions simulate async API calls with a small delay.
 *
 * Swap to real API: replace each function body with the corresponding
 * apiClient call (see INT-I-01 → INT-I-05 in the PRD).
 */
export const mockIntegrationsService = {
  // --- Companies ---

  async getCompanies(): Promise<CompanyWithIntegrations[]> {
    await delay(600);
    return [..._companiesStore];
  },

  async getCompany(id: string): Promise<CompanyWithIntegrations | undefined> {
    await delay(300);
    return _companiesStore.find((c) => c.id === id);
  },

  async createCompany(data: {
    name: string;
    code: string;
    status: 'active' | 'inactive';
  }): Promise<CompanyWithIntegrations> {
    await delay(600);
    const newCompany: CompanyWithIntegrations = {
      id: `company-${Date.now()}`,
      name: data.name,
      code: data.code.toUpperCase(),
      status: data.status,
      financialIntegration: { product: null, externalInstanceId: null, status: 'not_configured', lastSyncAt: null },
      hrIntegration:        { product: null, externalInstanceId: null, status: 'not_configured', lastSyncAt: null },
      projectIntegration:   { product: null, externalInstanceId: null, status: 'not_configured', lastSyncAt: null },
      plannerIntegration:   { product: null, externalInstanceId: null, status: 'not_configured', lastSyncAt: null },
      userCount: 0,
    };
    _companiesStore.push(newCompany);
    _companyUsersStore[newCompany.id] = [];
    return newCompany;
  },

  async updateCompany(
    id: string,
    data: Partial<Pick<CompanyWithIntegrations, 'name' | 'code' | 'status'>>
  ): Promise<CompanyWithIntegrations | undefined> {
    await delay(400);
    const index = _companiesStore.findIndex((c) => c.id === id);
    if (index === -1) return undefined;
    _companiesStore[index] = { ..._companiesStore[index], ...data };
    return _companiesStore[index];
  },

  // --- Integrations ---

  async updateIntegrations(
    companyId: string,
    financial: { product: string | null; externalInstanceId: string | null },
    hr: { product: string | null; externalInstanceId: string | null },
    project: { product: string | null; externalInstanceId: string | null },
    planner: { product: string | null; externalInstanceId: string | null }
  ): Promise<CompanyWithIntegrations | undefined> {
    await delay(500);
    const index = _companiesStore.findIndex((c) => c.id === companyId);
    if (index === -1) return undefined;

    _companiesStore[index] = {
      ..._companiesStore[index],
      financialIntegration: {
        ..._companiesStore[index].financialIntegration,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        product: financial.product as any,
        externalInstanceId: financial.externalInstanceId,
        status: financial.product && financial.externalInstanceId ? 'not_connected' : 'not_configured',
      },
      hrIntegration: {
        ..._companiesStore[index].hrIntegration,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        product: hr.product as any,
        externalInstanceId: hr.externalInstanceId,
        status: hr.product && hr.externalInstanceId ? 'not_connected' : 'not_configured',
      },
      projectIntegration: {
        ..._companiesStore[index].projectIntegration,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        product: project.product as any,
        externalInstanceId: project.externalInstanceId,
        status: project.product && project.externalInstanceId ? 'not_connected' : 'not_configured',
      },
      plannerIntegration: {
        ..._companiesStore[index].plannerIntegration,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        product: planner.product as any,
        externalInstanceId: planner.externalInstanceId,
        status: planner.product && planner.externalInstanceId ? 'not_connected' : 'not_configured',
      },
    };
    return _companiesStore[index];
  },

  async removeIntegration(
    companyId: string,
    type: 'financial' | 'hr' | 'project' | 'planner'
  ): Promise<CompanyWithIntegrations | undefined> {
    await delay(400);
    const index = _companiesStore.findIndex((c) => c.id === companyId);
    if (index === -1) return undefined;

    const cleared = { product: null, externalInstanceId: null, status: 'not_configured' as const, lastSyncAt: null };

    if (type === 'financial') {
      _companiesStore[index].financialIntegration = cleared;
    } else if (type === 'hr') {
      _companiesStore[index].hrIntegration = cleared;
    } else if (type === 'project') {
      _companiesStore[index].projectIntegration = cleared;
    } else {
      _companiesStore[index].plannerIntegration = cleared;
    }

    return _companiesStore[index];
  },

  async testConnection(
    companyId: string,
    type: 'financial' | 'hr' | 'project' | 'planner'
  ): Promise<{ success: boolean; message: string; tsoVersion?: string }> {
    await delay(1500);
    const company = _companiesStore.find((c) => c.id === companyId);
    if (!company) return { success: false, message: 'Company not found' };

    const integration =
      type === 'financial'
        ? company.financialIntegration
        : type === 'hr'
          ? company.hrIntegration
          : type === 'project'
            ? company.projectIntegration
            : company.plannerIntegration;

    if (!integration.product || !integration.externalInstanceId) {
      return { success: false, message: 'Integration not configured' };
    }

    // Simulate: 80% success rate in mock
    const success = Math.random() > 0.2;

    // Update the status in-store
    const index = _companiesStore.findIndex((c) => c.id === companyId);
    if (index !== -1) {
      if (type === 'financial') {
        _companiesStore[index].financialIntegration = {
          ..._companiesStore[index].financialIntegration,
          status: success ? 'connected' : 'error',
          lastSyncAt: success ? new Date().toISOString() : _companiesStore[index].financialIntegration.lastSyncAt,
        };
      } else if (type === 'hr') {
        _companiesStore[index].hrIntegration = {
          ..._companiesStore[index].hrIntegration,
          status: success ? 'connected' : 'error',
          lastSyncAt: success ? new Date().toISOString() : _companiesStore[index].hrIntegration.lastSyncAt,
        };
      } else if (type === 'project') {
        _companiesStore[index].projectIntegration = {
          ..._companiesStore[index].projectIntegration,
          status: success ? 'connected' : 'error',
          lastSyncAt: success ? new Date().toISOString() : _companiesStore[index].projectIntegration.lastSyncAt,
        };
      } else {
        _companiesStore[index].plannerIntegration = {
          ..._companiesStore[index].plannerIntegration,
          status: success ? 'connected' : 'error',
          lastSyncAt: success ? new Date().toISOString() : _companiesStore[index].plannerIntegration.lastSyncAt,
        };
      }
    }

    return {
      success,
      message: success
        ? 'Connection test successful (Version: 2.0)'
        : 'Connection test failed: Version 6.0 is out of supported range (must be > 1.2 and < 5)',
      tsoVersion: success ? '2.0' : '6.0',
    };
  },

  async syncIntegration(
    companyId: string,
    type: 'financial' | 'hr' | 'project' | 'planner'
  ): Promise<{ success: boolean; message: string; syncedAt: string | null; updatedCompany: CompanyWithIntegrations | undefined }> {
    await delay(2000); // Simulate a sync taking a moment
    const index = _companiesStore.findIndex((c) => c.id === companyId);
    if (index === -1) return { success: false, message: 'Company not found', syncedAt: null, updatedCompany: undefined };

    // Simulate: 90% success rate for sync
    const success = Math.random() > 0.1;
    const syncedAt = success ? new Date().toISOString() : null;

    if (type === 'financial') {
      _companiesStore[index].financialIntegration = {
        ..._companiesStore[index].financialIntegration,
        lastSyncAt: success ? syncedAt : _companiesStore[index].financialIntegration.lastSyncAt,
      };
    } else if (type === 'hr') {
      _companiesStore[index].hrIntegration = {
        ..._companiesStore[index].hrIntegration,
        lastSyncAt: success ? syncedAt : _companiesStore[index].hrIntegration.lastSyncAt,
      };
    } else if (type === 'project') {
      _companiesStore[index].projectIntegration = {
        ..._companiesStore[index].projectIntegration,
        lastSyncAt: success ? syncedAt : _companiesStore[index].projectIntegration.lastSyncAt,
      };
    } else {
      _companiesStore[index].plannerIntegration = {
        ..._companiesStore[index].plannerIntegration,
        lastSyncAt: success ? syncedAt : _companiesStore[index].plannerIntegration.lastSyncAt,
      };
    }

    return {
      success,
      message: success
        ? 'Sync completed successfully'
        : 'Sync failed — please try again or check your connection settings',
      syncedAt,
      updatedCompany: _companiesStore[index],
    };
  },

  // --- Company Users ---

  async getCompanyUsers(companyId: string): Promise<CompanyUser[]> {
    await delay(400);
    return [...(_companyUsersStore[companyId] ?? [])];
  },

  async addUserToCompany(companyId: string, userId: string): Promise<CompanyUser | undefined> {
    await delay(400);
    const user = mockAllUsers.find((u) => u.id === userId);
    if (!user) return undefined;

    const current = _companyUsersStore[companyId] ?? [];
    if (current.some((u) => u.id === userId)) return user; // Already added

    _companyUsersStore[companyId] = [...current, user];

    // Update userCount
    const companyIndex = _companiesStore.findIndex((c) => c.id === companyId);
    if (companyIndex !== -1) {
      _companiesStore[companyIndex] = {
        ..._companiesStore[companyIndex],
        userCount: _companyUsersStore[companyId].length,
      };
    }

    return user;
  },

  async removeUserFromCompany(companyId: string, userId: string): Promise<void> {
    await delay(400);
    const current = _companyUsersStore[companyId] ?? [];
    _companyUsersStore[companyId] = current.filter((u) => u.id !== userId);

    // Update userCount
    const companyIndex = _companiesStore.findIndex((c) => c.id === companyId);
    if (companyIndex !== -1) {
      _companiesStore[companyIndex] = {
        ..._companiesStore[companyIndex],
        userCount: _companyUsersStore[companyId].length,
      };
    }
  },

  async searchUsers(query: string, excludeCompanyId?: string): Promise<CompanyUser[]> {
    await delay(250);
    const q = query.toLowerCase();
    const excluded = excludeCompanyId ? (_companyUsersStore[excludeCompanyId] ?? []).map((u) => u.id) : [];
    return mockAllUsers.filter(
      (u) =>
        !excluded.includes(u.id) &&
        (u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) || u.role.toLowerCase().includes(q))
    );
  },

  // --- Employee Company Access ---

  async getEmployeeCompanyAccess(employeeId: string): Promise<EmployeeCompanyAccessRow[]> {
    await delay(400);
    return getMockEmployeeCompanyAccess(employeeId);
  },

  async updateEmployeeCompanyAccess(
    employeeId: string,
    updates: Array<{ companyId: string; hasAccess: boolean; isDefault: boolean }>
  ): Promise<EmployeeCompanyAccessRow[]> {
    await delay(500);
    // In a real implementation this would call PATCH /employees/{id}/company-access
    // For mock, we just return the updated rows directly
    return _companiesStore.map((company) => {
      const update = updates.find((u) => u.companyId === company.id);
      if (update) {
        return {
          companyId: company.id,
          companyName: company.name,
          companyCode: company.code,
          companyStatus: company.status,
          hasAccess: update.hasAccess,
          isDefault: update.isDefault,
        };
      }
      // Fallback: check original mock
      const original = getMockEmployeeCompanyAccess(employeeId).find(
        (r) => r.companyId === company.id
      );
      return original ?? {
        companyId: company.id,
        companyName: company.name,
        companyCode: company.code,
        companyStatus: company.status,
        hasAccess: false,
        isDefault: false,
      };
    });
  },
};
